import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'sonner';

import { useFormWithValidation, useProjectSiteParams } from '@/shared/lib';
import { type Contact, useCreateContactMutation, useUpdateContactMutation } from '@/entities/contacts';

export const contactSchema = (t: unknown) =>
  z.object({
    title: z.string().optional(),
    name: z.string().min(1, t('validation.required', 'Name is mandatory')),
    jobTitle: z.string().optional(),
    address: z.string().optional(),
    email1: z
      .string()
      .min(1, t('validation.required', 'Primary Email is mandatory'))
      .email(t('validation.invalidEmail', 'Invalid email format')),
    email2: z.string().email(t('validation.invalidEmail', 'Invalid email format')).optional().or(z.literal('')),
    email3: z.string().email(t('validation.invalidEmail', 'Invalid email format')).optional().or(z.literal('')),
    phone1: z.string().min(1, t('validation.required', 'Primary Phone is mandatory')),
    phone2: z.string().optional(),
    phone3: z.string().optional(),
    orgId: z.number().optional()
  });

export type ContactFormValues = z.infer<ReturnType<typeof contactSchema>>;

interface UseContactFormProps {
  initialData?: Partial<Contact>;
  onSuccess: (contact: Contact) => void;
  onCancel: () => void;
}

export const useContactForm = ({ initialData, onSuccess, onCancel }: UseContactFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();

  const form = useFormWithValidation(contactSchema(t), {
    title: '',
    name: '',
    jobTitle: '',
    address: '',
    email1: '',
    email2: '',
    email3: '',
    phone1: '',
    phone2: '',
    phone3: '',
    orgId: undefined
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        title: initialData.title || '',
        name: initialData.name || '',
        jobTitle: initialData.jobTitle || '',
        address: initialData.address || '',
        email1: initialData.email1 || '',
        email2: initialData.email2 || '',
        email3: initialData.email3 || '',
        phone1: initialData.phone1 || '',
        phone2: initialData.phone2 || '',
        phone3: initialData.phone3 || '',
        orgId: initialData.orgId
      });
    }
  }, [initialData, form]);

  const onSubmit = useCallback(
    async (values: ContactFormValues) => {
      if (!projectId || !siteId) {
        toast.error(t('common.errors.missingParams', 'Missing required parameters'));
        return;
      }

      try {
        let result: Contact;
        if (initialData?.id) {
          result = await updateContact({
            ...values,
            id: initialData.id,
            projectId: projectId as number,
            siteId: siteId as number
          }).unwrap();
          toast.success(t('contacts.updateSuccess', 'Contact updated successfully'));
        } else {
          result = await createContact({
            ...values,
            projectId: projectId as number,
            siteId: siteId as number
          }).unwrap();
          toast.success(t('contacts.createSuccess', 'Contact created successfully'));
        }
        onSuccess(result);
      } catch (error) {
        toast.error(t('contacts.saveError', 'Failed to save contact'));
        console.error('Failed to save contact:', error);
      }
    },
    [initialData, projectId, siteId, createContact, updateContact, onSuccess, t]
  );

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSaving: isCreating || isUpdating,
    onCancel
  };
};
